// ブラシ関連のシェーダ。
// - DAB: 先端テクスチャをインスタンス描画してカバレッジ (R8) に source-over で蓄積
// - PAINT: レイヤー (ストレートアルファ) にカバレッジ × 不透明度で色を乗せる / 消す

export const DAB_VERT = /* glsl */ `#version 300 es
layout(location = 0) in vec2 aUnit;      // 0..1
layout(location = 1) in vec2 aCenter;    // 文書座標
layout(location = 2) in float aSize;     // 直径
layout(location = 3) in float aAlpha;
uniform mat3 uToClip;
uniform float uAngle;      // 先端の回転
uniform float uRoundness;  // 真円率 (短軸 / 長軸)
out vec2 vUv;
out float vAlpha;
void main() {
  vec2 local = (aUnit - 0.5) * aSize * vec2(1.0, uRoundness);
  float rc = cos(uAngle), rs = sin(uAngle);
  vec2 pos = aCenter + vec2(local.x * rc - local.y * rs, local.x * rs + local.y * rc);
  vUv = aUnit;
  vAlpha = aAlpha;
  vec3 c = uToClip * vec3(pos, 1.0);
  gl_Position = vec4(c.xy, 0.0, 1.0);
}
`

export const DAB_FRAG = /* glsl */ `#version 300 es
precision highp float;
in vec2 vUv;
in float vAlpha;
uniform sampler2D uTip;
uniform int uPencil;
out vec4 outColor;
void main() {
  float t = texture(uTip, vUv).r;
  if (uPencil == 1) t = step(0.5, t);
  float a = t * vAlpha;
  outColor = vec4(a, 0.0, 0.0, a);
}
`

export const PAINT_FRAG = /* glsl */ `#version 300 es
precision highp float;
in vec2 vPos;                 // 文書座標
uniform sampler2D uDst;       // レイヤー (文書空間、ストレートアルファ) = ストローク開始時の状態
uniform sampler2D uCov;       // カバレッジ R8 (文書空間)
uniform sampler2D uSel;       // 選択マスク R8
uniform vec4 uSelRect;
uniform int uHasSel;
uniform vec2 uDocSize;
uniform vec3 uColor;
uniform float uOpacity;
uniform int uMode;            // 0 paint, 1 erase, 2 clone, 3 dodge, 4 burn, 5 sponge(+), 6 sponge(-), 7 blur, 8 sharpen
uniform vec2 uCloneOffset;    // clone: ソース位置 = 現在位置 - offset
uniform float uStrength;      // dodge/burn/sponge/blur/sharpen の強さ 0..1
uniform int uLockTransparency;
out vec4 outColor;

float lum(vec3 c) { return dot(c, vec3(0.299, 0.587, 0.114)); }
vec4 at(vec2 p) {
  vec2 uv = p / uDocSize;
  if (uv.x < 0.0 || uv.y < 0.0 || uv.x > 1.0 || uv.y > 1.0) return vec4(0.0);
  return texture(uDst, uv);
}
vec4 blurAt(vec2 p) {
  // 5x5 のプリマルチ平均
  vec4 sum = vec4(0.0);
  for (int j = -2; j <= 2; j++) for (int i = -2; i <= 2; i++) {
    vec4 c = at(p + vec2(float(i), float(j)));
    sum += vec4(c.rgb * c.a, c.a);
  }
  sum /= 25.0;
  return sum.a > 0.0 ? vec4(sum.rgb / sum.a, sum.a) : vec4(0.0);
}

void main() {
  vec2 uv = vPos / uDocSize;
  vec4 d = texture(uDst, uv);
  float cov = texture(uCov, uv).r;
  float sel = 1.0;
  if (uHasSel == 1) {
    vec2 uvS = (vPos - uSelRect.xy) / uSelRect.zw;
    sel = (uvS.x >= 0.0 && uvS.x <= 1.0 && uvS.y >= 0.0 && uvS.y <= 1.0) ? texture(uSel, uvS).r : 0.0;
  }
  float a = clamp(cov * uOpacity * sel, 0.0, 1.0);
  if (uLockTransparency == 1) a *= d.a;
  if (uMode == 1) { outColor = vec4(d.rgb, d.a * (1.0 - a)); return; }
  if (uMode == 2) {
    // コピースタンプ: ソースの色と α で source-over
    vec4 s = at(vPos - uCloneOffset);
    float as = a * s.a;
    float oa = as + d.a * (1.0 - as);
    vec3 oc = oa > 0.0 ? (s.rgb * as + d.rgb * d.a * (1.0 - as)) / oa : vec3(0.0);
    outColor = vec4(oc, oa);
    return;
  }
  if (uMode >= 3) {
    // 色を変えるだけのモード (α は保つ)。k = 強さ × カバレッジ
    float k = clamp(a * uStrength, 0.0, 1.0);
    vec3 c = d.rgb;
    vec3 o = c;
    if (uMode == 3) o = pow(c, vec3(1.0 / (1.0 + k * 2.0)));          // 覆い焼き: 明るく
    else if (uMode == 4) o = pow(c, vec3(1.0 + k * 2.0));             // 焼き込み: 暗く
    else if (uMode == 5) { float l = lum(c); o = l + (c - l) * (1.0 + k * 2.0); }   // スポンジ (彩度を上げる)
    else if (uMode == 6) { float l = lum(c); o = mix(c, vec3(l), k); }              // スポンジ (彩度を下げる)
    else if (uMode == 7) { vec4 b = blurAt(vPos); o = mix(c, b.rgb, k); }           // ぼかし
    else if (uMode == 8) { vec4 b = blurAt(vPos); o = c + (c - b.rgb) * k * 2.0; }  // シャープ
    outColor = vec4(clamp(o, 0.0, 1.0), d.a);
    return;
  }
  float oa = a + d.a * (1.0 - a);
  vec3 oc = oa > 0.0 ? (uColor * a + d.rgb * d.a * (1.0 - a)) / oa : vec3(0.0);
  outColor = vec4(oc, oa);
}
`
